import React from "react";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import SearchIcon from "@mui/icons-material/Search";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import FilterListIcon from "@mui/icons-material/FilterList";
import ModuleEditorModal from "./ModuleEditorModal";
import { TrainingEmptyState } from "./TrainingEmptyState";
import LearningContentPreviewModal from "./LearningContentPreviewModal";
import LearningPreviewMenuButton from "./LearningPreviewMenuButton";
import { formatDate } from "../requests/utilities/helpers";
import LibraryFilterChips from "./LibraryFilterChips";

const ASSIGNABLE_OPTIONS = [
  { value: "assignable", label: "Assignable" },
  { value: "not_assignable", label: "Not assignable" },
  { value: "unassigned", label: "Not in tracks" },
];

const filterSelectSx = {
  height: 42,
  minWidth: 170,
  borderRadius: "8px",
  backgroundColor: "white",
  fontSize: 14,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#a78bfa" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
  "& .MuiSelect-select": {
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "12px",
  },
};

function ModulesDisplay(props) {
  const [openEditor, setOpenEditor] = React.useState(false);
  const [addMenuAnchor, setAddMenuAnchor] = React.useState(null);
  const [previewModule, setPreviewModule] = React.useState(null);
  const [searchInput, setSearchInput] = React.useState("");
  const [categoryFilter, setCategoryFilter] = React.useState("all");
  const [assignableFilter, setAssignableFilter] = React.useState("all");
  const [sortKey, setSortKey] = React.useState("recent");

  const modules = React.useMemo(
    () => props.modulesData || [],
    [props.modulesData]
  );

  const categories = React.useMemo(() => {
    return [...new Set(modules.map((module) => module.category).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b));
  }, [modules]);

  const filteredModules = React.useMemo(() => {
    const searchParts = searchInput.toLowerCase().split(" ").filter(Boolean);
    const filtered = modules.filter((module) => {
      const haystack = [
        module.name,
        module.description,
        module.category,
        ...(module.sections || []).map((section) => section.name),
        ...(module.tracks || []).map((track) => track.name),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (!searchParts.every((part) => haystack.includes(part))) return false;
      if (categoryFilter !== "all" && (module.category || "") !== categoryFilter) {
        return false;
      }
      if (assignableFilter === "assignable" && !module.is_directly_assignable) {
        return false;
      }
      if (assignableFilter === "not_assignable" && module.is_directly_assignable) {
        return false;
      }
      if (assignableFilter === "unassigned" && (module.tracks || []).length > 0) {
        return false;
      }
      return true;
    });

    return [...filtered].sort((a, b) => {
      if (sortKey === "name") {
        return (a.name || "").localeCompare(b.name || "");
      }
      if (sortKey === "sections") {
        return (b.sections?.length || 0) - (a.sections?.length || 0);
      }
      if (sortKey === "tracks") {
        return (b.tracks?.length || 0) - (a.tracks?.length || 0);
      }
      return (b.updated_at || b.created_at || 0) - (a.updated_at || a.created_at || 0);
    });
  }, [modules, searchInput, categoryFilter, assignableFilter, sortKey]);

  const openModuleEditor = (module) => {
    props.setModalDataModule(module);
    setOpenEditor(true);
  };

  const activeFilterCount =
    (categoryFilter !== "all" ? 1 : 0) +
    (assignableFilter !== "all" ? 1 : 0);

  return (
    <>
      <div className="min-h-screen bg-white">
        <div className="bg-white border-b border-gray-200">
          <div className="px-6 py-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-slate-900 mb-1">
                  Learning Modules
                </h1>
                <p className="text-sm text-slate-600">
                  Chapter-level groupings that can be assigned or reused in tracks
                </p>
              </div>
              <button
                className="bg-violet-700 hover:bg-violet-800 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
                onClick={(e) => setAddMenuAnchor(e.currentTarget)}
              >
                <AddIcon style={{ fontSize: "20px" }} />
                <span className="font-medium">New Module</span>
                <KeyboardArrowDownIcon style={{ fontSize: "18px" }} />
              </button>
              <Menu
                anchorEl={addMenuAnchor}
                open={Boolean(addMenuAnchor)}
                onClose={() => setAddMenuAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem
                  onClick={() => {
                    setAddMenuAnchor(null);
                    props.setModalDataModule(props.default_data_module);
                    setOpenEditor(true);
                  }}
                >
                  <ListItemIcon>
                    <AddIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="New module" secondary="Build it yourself" />
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setAddMenuAnchor(null);
                    props.onGenerateWithAi && props.onGenerateWithAi();
                  }}
                >
                  <ListItemIcon>
                    <AutoAwesomeIcon fontSize="small" sx={{ color: "#6d28d9" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Generate with AI"
                    secondary="Draft a module from a prompt and your docs"
                  />
                </MenuItem>
              </Menu>
            </div>

            <div className="flex items-center gap-6 text-sm text-slate-600 mb-6">
              <div className="flex items-center gap-2">
                <ViewModuleIcon style={{ fontSize: "18px" }} />
                <span>
                  {filteredModules.length}{" "}
                  {filteredModules.length === 1 ? "Module" : "Modules"}
                </span>
              </div>
              {searchInput && (
                <div className="flex items-center gap-2 text-violet-600">
                  <SearchIcon style={{ fontSize: "16px" }} />
                  <span>Showing search results</span>
                </div>
              )}
              {activeFilterCount > 0 && (
                <div className="flex items-center gap-2 text-violet-600">
                  <FilterListIcon style={{ fontSize: "16px" }} />
                  <span>
                  {activeFilterCount}{" "}
                  {activeFilterCount === 1 ? "filter" : "filters"} active
                </span>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="relative w-full max-w-md">
                <SearchIcon
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  style={{ fontSize: "20px" }}
                />
                <input
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-200 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-100 text-sm"
                  type="text"
                  placeholder="Search modules..."
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                />
              </div>

              <Select
                size="small"
                displayEmpty
                sx={filterSelectSx}
                value={categoryFilter}
                onChange={(event) => setCategoryFilter(event.target.value)}
              >
                <MenuItem value="all" sx={{ fontSize: 14 }}>
                  All categories
                </MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category} value={category} sx={{ fontSize: 14 }}>
                    {category}
                  </MenuItem>
                ))}
              </Select>

              <Select
                size="small"
                sx={filterSelectSx}
                value={sortKey}
                onChange={(event) => setSortKey(event.target.value)}
              >
                <MenuItem value="recent" sx={{ fontSize: 14 }}>
                  Recently updated
                </MenuItem>
                <MenuItem value="name" sx={{ fontSize: 14 }}>Name</MenuItem>
                <MenuItem value="sections" sx={{ fontSize: 14 }}>
                  Most sections
                </MenuItem>
                <MenuItem value="tracks" sx={{ fontSize: 14 }}>
                  Most tracks
                </MenuItem>
              </Select>
            </div>

            <div className="mt-4">
              <LibraryFilterChips
                groups={[
                  {
                    key: "assignable",
                    value: assignableFilter,
                    onChange: setAssignableFilter,
                    options: ASSIGNABLE_OPTIONS,
                  },
                ]}
              />
            </div>
          </div>
        </div>

        <div className="px-6 py-8">
          {filteredModules.length === 0 ? (
            <TrainingEmptyState
              icon={ViewModuleIcon}
              plural="modules"
              blurb="Create reusable chapters that group related training sections."
              createLabel="Create your first module"
              searching={Boolean(searchInput || activeFilterCount)}
              onCreate={() => {
                props.setModalDataModule(props.default_data_module);
                setOpenEditor(true);
              }}
            />
          ) : (
            <div className="grid grid-cols-3 xl:grid-cols-2 md:grid-cols-1 gap-6">
              {filteredModules.map((module) => (
                <ModuleCard
                  key={module.uuid}
                  module={module}
                  onEdit={() => openModuleEditor(module)}
                  onPreview={(device) => setPreviewModule({ item: module, device })}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <ModuleEditorModal
        open={openEditor}
        handleClose={() => setOpenEditor(false)}
        modalDataModule={props.modalDataModule}
        setModalDataModule={props.setModalDataModule}
        homeCompany={props.homeCompany}
        homeStore={props.homeStore}
        modalDataSection={props.modalDataSection}
        setModalDataSection={props.setModalDataSection}
        default_data_module={props.default_data_module}
        default_data_section={props.default_data_section}
        sectionsData={props.sectionsData}
        post_learning_module={props.post_learning_module}
        delete_learning_module={props.delete_learning_module}
        post_learning_section={props.post_learning_section}
        delete_learning_section={props.delete_learning_section}
        modalDataQuiz={props.modalDataQuiz}
        setModalDataQuiz={props.setModalDataQuiz}
        default_data_quiz={props.default_data_quiz}
        post_learning_quiz={props.post_learning_quiz}
        quizzesData={props.quizzesData}
        modulesData={props.modulesData}
      />

      <LearningContentPreviewModal
        open={Boolean(previewModule)}
        onClose={() => setPreviewModule(null)}
        previewType="module"
        item={previewModule?.item}
        initialDevice={previewModule?.device}
        homeCompany={props.homeCompany}
        quizzesData={props.quizzesData}
      />
    </>
  );
}

function ModuleCard({ module, onEdit, onPreview }) {
  const sectionCount = module.sections?.length || 0;
  const trackCount = module.tracks?.length || 0;
  const color = module.color || "bg-violet-700";
  const updatedLabel = module.updated_at
    ? `Updated ${formatDate(module.updated_at)}`
    : "";

  return (
    <div
      className="group cursor-pointer overflow-hidden rounded-lg border border-slate-200 bg-white transition-all duration-200 hover:border-slate-300 hover:shadow-lg"
      onClick={onEdit}
    >
      <div className={`relative h-36 overflow-hidden ${color}`}>
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-4 text-white">
          <div className="mb-3 flex items-center gap-2">
            {module.is_directly_assignable && (
              <span className="rounded-full bg-white/90 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                Assignable
              </span>
            )}
            {module.category && (
              <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs font-medium text-white">
                {module.category}
              </span>
            )}
          </div>
          <h3 className="line-clamp-2 text-lg font-semibold drop-shadow-sm">
            {module.name || "Untitled module"}
          </h3>
        </div>
        <div className="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          <LearningPreviewMenuButton
            onSelect={onPreview}
            iconButtonSx={{
              backgroundColor: "rgba(255,255,255,0.92)",
              "&:hover": { backgroundColor: "white" },
            }}
          />
          <Tooltip title="Edit module">
            <span>
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  onEdit();
                }}
                sx={{
                  backgroundColor: "rgba(255,255,255,0.92)",
                  "&:hover": { backgroundColor: "white" },
                }}
              >
                <EditIcon style={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>

      <div className="p-4">
        {module.description ? (
          <p className="line-clamp-2 text-sm text-slate-600">
            {module.description}
          </p>
        ) : (
          <p className="text-sm italic text-slate-400">No description yet</p>
        )}

        <div className="mt-4 flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-wrap gap-2">
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
              {sectionCount} {sectionCount === 1 ? "section" : "sections"}
            </span>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
              {trackCount > 0
                ? `${trackCount} ${trackCount === 1 ? "track" : "tracks"}`
                : "Not in tracks"}
            </span>
          </div>
          {updatedLabel && (
            <span className="shrink-0 text-xs text-slate-400">{updatedLabel}</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default ModulesDisplay;
